import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/navbar';
import toast from 'react-hot-toast';
import { FaPlus, FaEdit, FaMinus, FaTrash } from 'react-icons/fa';

function PdiReports() {
  const [pdiDetails, setPdiDetails] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState({});
  const [filterOption, setFilterOption] = useState('all');
  const [deletePdiId, setDeletePdiId] = useState(null);
  const [sortOrder, setSortOrder] = useState('latest');
  const [expandedReport, setExpandedReport] = useState(null);
  
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  // Fields to exclude from display and editing
  const EXCLUDED_FIELDS = [
    '_id', '__v', 'pdfUrl', 'fileName', 'updatedAt', 
    'serviceId', 'userId', 'technicianId', 'technicianName',
    'createdAt'
  ];

  // Fetch PDI Reports
  useEffect(() => {
    const fetchPdiDetails = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/pdi-reports/getAll`);
        const pdis = response.data;
        setPdiDetails(pdis);
      } catch (err) {
        setError('Error fetching PDI details');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPdiDetails();
  }, []);

  // Fetch Technicians
  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/technicians`);
        if (Array.isArray(response.data)) {
          setTechnicians(response.data);
        } else {
          setError('Technicians data is not in the expected format');
        }
      } catch (err) {
        setError('Error fetching technicians');
        console.error('Technician fetch error:', err);
      }
    };

    fetchTechnicians();
  }, []);

  // Regenerate PDF URL if expired
  const regeneratePdfUrl = async (pdiReportId) => {
    try {
      const response = await axios.patch(`${API_URL}/api/pdi-reports/regeneratePDIReport/${pdiReportId}`);
      return response.data.pdfUrl;
    } catch (error) {
      console.error("Failed to regenerate PDF URL", error);
      return null;
    }
  };

  // Main handler
  const handlePdfDownload = async (pdi) => {
    try {
      let validUrl = pdi.pdfUrl;

      const isUrlExpired = async (url) => {
        try {
          const res = await fetch(url, { method: 'HEAD' });
          return !res.ok;
        } catch {
          return true;
        }
      };

      if (await isUrlExpired(validUrl)) {
        validUrl = await regeneratePdfUrl(pdi._id);
        if (!validUrl) {
          toast.error("Unable to fetch a valid PDF link.");
          return;
        }
      }

      // Force download using an <a> tag
      const link = document.createElement("a");
      link.href = validUrl;
      link.setAttribute("download", `Report_${pdi._id}.pdf`); // optional custom file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error("Failed to download PDF:", error);
      toast.error("Something went wrong while downloading the report.");
    }
  };

  // Assign Technician to PDI
  const assignPdiToTechnician = async (pdiId, technicianId) => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/pdi-reports/${pdiId}/assign-technician`, 
        { technicianId }
      );
      
      // Update local state with new assignment
      setPdiDetails(prev => 
        prev.map(pdi => 
          pdi._id === pdiId ? { ...pdi, technicianId: response.data.technicianId } : pdi
        )
      );
      
      // Clear selection state
      setSelectedTechnician(prev => {
        const newState = { ...prev };
        delete newState[pdiId];
        return newState;
      });
    } catch (err) {
      setError(`Error assigning technician to PDI ${pdiId}`);
      console.error('Assign error:', err);
    }
  };

  const handleTechnicianChange = (pdiId, technicianId) => {
    setSelectedTechnician(prev => ({
      ...prev,
      [pdiId]: technicianId,
    }));
  };

  // Navigate to edit page based on inspection type
  const handleEditClick = (pdi) => {
    switch (pdi.inspectionType) {
      case "Pre Delivery Inspection":
        navigate(`/admin/report-pre-delivery/${pdi._id}`);
        break;
      case "Used Car Inspections":
        navigate(`/admin/report-used-car/${pdi._id}`);
        break;
      case "Pre Trip Inspections":
        navigate(`/admin/report-pre-trip/${pdi._id}`);
        break;
      default:
        console.error(`Unhandled inspection type: ${pdi.inspectionType}`);
        toast.error("No edit form available for this inspection type");
        break;
    }
  };

  const handleFilterChange = (e) => {
    setFilterOption(e.target.value);
  };

  // Filter reports based on selection
  const filteredPdiReports = pdiDetails.filter(pdi => {
    if (filterOption === 'completed') return pdi.status === 'Completed';
    if (filterOption === 'unassigned') return !pdi.technicianId;
    return true; // 'all' option
  });

  // Sort reports by creation date
  const sortedServices = [...filteredPdiReports].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
  });

  const handleDeletePdi = async () => {
    try {
      await axios.delete(`${API_URL}/api/pdi-reports/${deletePdiId}`);
      setPdiDetails(prev => prev.filter(pdi => pdi._id !== deletePdiId));
      setDeletePdiId(null);
      toast.success("PDI report deleted successfully");
    } catch (err) {
      console.error('Error deleting PDI:', err);
      setError('Failed to delete the PDI');
      toast.error("Failed to delete PDI report");
    }
  };

  const toggleAccordion = (pdiId) => {
    setExpandedReport(expandedReport === pdiId ? null : pdiId);
  };

  // Convert camelCase to readable format
  const formatFieldName = (fieldName) => {
    const result = fieldName.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  // Format field values for display
  const formatFieldValue = (key, value) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Format dates
    if (key.toLowerCase().includes('date') || key.toLowerCase().includes('at')) {
      return new Date(value).toLocaleString();
    }
    
    return value;
  };

  // Get all fields from a PDI object except excluded ones
  const getPdiFields = (pdi) => {
    // Create a display object that merges top-level and reportData properties
    const displayData = { 
      ...pdi,
      ...(pdi.reportData || {})
    };

    return Object.entries(displayData)
      .filter(([key]) => !EXCLUDED_FIELDS.includes(key))
      .map(([key, value]) => ({
        name: key,
        label: formatFieldName(key),
        value: formatFieldValue(key, value)
      }));
  };

  // Group fields into sections for better organization
  const groupFields = (fields) => {
    const customerFields = fields.filter(field => 
      field.name.toLowerCase().includes('customer') || 
      field.name.toLowerCase().includes('address') ||
      field.name.toLowerCase().includes('phone')
    );
    
    const vehicleFields = fields.filter(field => 
      field.name.toLowerCase().includes('vehicle') || 
      field.name.toLowerCase().includes('model') ||
      field.name.toLowerCase().includes('variant') ||
      field.name.toLowerCase().includes('vin') ||
      field.name.toLowerCase().includes('engine')
    );
    
    // const metaFields = fields.filter(field => 
    //   field.name.toLowerCase().includes('createdat') || 
    //   field.name.toLowerCase().includes('updatedat') ||
    //   field.name.toLowerCase().includes('status')
    // );
    
    // Removed reportFields section as requested
    
    return [
      { title: 'Customer Details', fields: customerFields },
      { title: 'Vehicle Details', fields: vehicleFields },
      // { title: 'Metadata', fields: metaFields }
    ];
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />
      <div className="p-6 pt-28 max-w-6xl mx-auto">

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">       
          <h1 className="text-2xl font-bold mb-6">PDI Reports</h1>

          <div className="flex flex-wrap gap-4 mb-6">
            <div>
              <label htmlFor="filter" className="block mb-1 text-sm font-medium">Filter Reports:</label>
              <select 
                id="filter" 
                value={filterOption} 
                onChange={handleFilterChange}
                className="border rounded px-3 py-2 w-48"
              >
                <option value="all">All Reports</option>
                <option value="completed">Completed Reports</option>
                <option value="unassigned">Unassigned Reports</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="sort" className="block mb-1 text-sm font-medium">Sort By:</label>
              <select 
                id="sort" 
                value={sortOrder} 
                onChange={(e) => setSortOrder(e.target.value)}
                className="border rounded px-3 py-2 w-48"
              >
                <option value="latest">Latest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </div>
        </div> 

        {loading && 
          <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        }
        {error && 
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>        
        }

        {!loading && sortedServices.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No PDI reports found</h3>
              <p className="mt-1 text-sm text-gray-500">
                There are currently no {filterOption} tasks.
              </p>
            </div>
        )}

        <div className="space-y-4">
          {sortedServices.map((pdi) => {
            // Safely get customerName and vehicleModel
            const customerName = pdi.reportData?.customerName || pdi.customerName;
            const vehicleModel = pdi.reportData?.vehicleModel || pdi.vehicleModel;
            
            const allFields = getPdiFields(pdi);
            const groupedFields = groupFields(allFields);
            
            return (
              <div key={pdi._id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
                <button
                  className="w-full flex justify-between items-center p-4 bg-white hover:bg-gray-100 transition-colors"
                  onClick={() => toggleAccordion(pdi._id)}
                >
                  <div className="text-left">
                    <h3 className="font-semibold text-lg text-gray-900">
                      {customerName || 'Unnamed Report'} - {vehicleModel || 'No Vehicle'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Created: {new Date(pdi.createdAt).toLocaleDateString()} &nbsp; | &nbsp;
                      {pdi.inspectionType || 'No type'}
                    </p>
                  </div>
                  <span className="text-xl transform transition-transform">
                    {expandedReport === pdi._id ? <FaMinus /> : <FaPlus />}
                  </span>
                </button>

                {expandedReport === pdi._id && (
                  <div className="p-4 bg-white border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {groupedFields.map((section, idx) => (
                        section.fields.length > 0 && (
                          <div key={idx}>
                            <h4 className="font-medium text-gray-900 mb-2">{section.title}</h4>
                            <div className="space-y-1 text-sm">
                              {section.fields.map((field) => (
                                <p key={field.name}>
                                  <span className="font-medium">{field.label}:</span> {field.value}
                                </p>
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                    </div>

                    <div className="mb-6">
                      {pdi.technicianId ? (
                        technicians
                          .filter(tech => tech._id === pdi.technicianId._id)
                          .map(tech => (
                            <p key={tech._id}>
                              <strong>Assigned Technician:</strong> {tech.firstname} {tech.lastname}
                            </p>
                          ))
                      ) : (
                        <p><strong>Assigned Technician:</strong> Not Assigned</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 mt-2 p-4 border-t border-gray-200">
                  <button
                    onClick={() => handleEditClick(pdi)}
                    className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 transition-colors text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                  >
                    <FaEdit />
                    Edit Report
                  </button>

                  {/* Download PDF Button */}
                  {pdi.pdfUrl && (
                    <button
                      onClick={() => handlePdfDownload(pdi)}
                      className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 hover:bg-green-50 transition-colors text-green-600 hover:text-green-800"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Download Report
                    </button>
                  )}
                  
                  <button
                    onClick={() => setDeletePdiId(pdi._id)}
                    className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <FaTrash />
                    Delete Report
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deletePdiId && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Confirm Deletion</h3>
            <p className="mb-6">Are you sure you want to delete this PDI report? This action cannot be undone.</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletePdiId(null)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePdi}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PdiReports;